import { useSortableContext } from '@app/components/various/Sortable/context/SortableContext';
import { isDefined } from '@app/utils/is';
import { buildTree, flattenTree, getMaxDepth } from '@app/utils/sortable';
import { arrayMove } from '@dnd-kit/sortable';
import { Sortable } from '@typings';
import { update } from 'ramda';
import { useTranslation } from 'react-i18next';

import { useSortableAnnouncements } from './useSortableAnnouncements';

interface Props {
  maxDepth: number;
  validateNesting?: Sortable.ValidateNesting;
  onSortEnd?: (items: Sortable.Items) => void;
}

export const useGetSortableItemMenu = ({ maxDepth: maxAllowedDepth, validateNesting, onSortEnd }: Props) => {
  const { t } = useTranslation(['accessibilityDescriptions']);

  const { flattenedItems } = useSortableContext();
  const { announceMovementChange, announceNestingChange } = useSortableAnnouncements();

  const getPrevSibling = (item: Sortable.FlattenedItem) => {
    const itemIndex = flattenedItems.indexOf(item);

    return flattenedItems
      .slice(0, itemIndex)
      .reverse()
      .find(({ parentId, index }) => index < item.index && parentId === item.parentId);
  };

  const getNextSibling = (item: Sortable.FlattenedItem) => {
    const itemIndex = flattenedItems.indexOf(item);

    return flattenedItems.slice(itemIndex).find(({ parentId, index }) => index > item.index && parentId === item.parentId);
  };

  const getCanNestToPrevSibling = (item: Sortable.FlattenedItem, prevSibling?: Sortable.FlattenedItem) => {
    if (!isDefined(prevSibling)) {
      return false;
    }

    const maxDepth = getMaxDepth({ activeItem: item, maxAllowedDepth, previousItem: prevSibling });

    if (maxDepth < item.depth + 1) {
      return false;
    }

    return !isDefined(validateNesting) || validateNesting(item.id, prevSibling.id).isValid;
  };

  const handleMoveUp = (item: Sortable.FlattenedItem) => {
    const itemIndex = flattenedItems.indexOf(item);
    const nextSibling = flattenedItems
      .slice(0, itemIndex)
      .reverse()
      .find(({ parentId }) => parentId === item.parentId);

    if (!isDefined(nextSibling)) {
      return;
    }

    const sortedItems = arrayMove(flattenedItems, itemIndex, flattenedItems.indexOf(nextSibling));
    const sortedTree = buildTree(sortedItems);

    onSortEnd?.(sortedTree);
    announceMovementChange(item, flattenTree(sortedTree));
  };

  const handleMoveDown = (item: Sortable.FlattenedItem) => {
    const itemIndex = flattenedItems.indexOf(item);
    const nextSibling = flattenedItems.slice(itemIndex + 1).find(({ parentId }) => parentId === item.parentId);

    if (!isDefined(nextSibling)) {
      return;
    }

    const sortedItems = arrayMove(flattenedItems, itemIndex, flattenedItems.indexOf(nextSibling));
    const sortedTree = buildTree(sortedItems);

    onSortEnd?.(sortedTree);
    announceMovementChange(item, flattenTree(sortedTree));
  };

  const handleNest = (item: Sortable.FlattenedItem, prevSibling?: Sortable.FlattenedItem) => {
    if (!isDefined(prevSibling)) {
      return;
    }

    const itemIndex = flattenedItems.indexOf(item);
    const parentIndex = flattenedItems.indexOf(prevSibling);

    const updatedItems = update(itemIndex, { ...item, depth: prevSibling.depth + 1, parentId: prevSibling.id }, flattenedItems);
    const sortedItems = arrayMove(updatedItems, itemIndex, parentIndex + (prevSibling.children?.length ?? 0) + 1);
    const sortedTree = buildTree(sortedItems);

    onSortEnd?.(sortedTree);
    announceNestingChange(item, flattenTree(sortedTree));
  };

  const handleUnNest = (item: Sortable.FlattenedItem) => {
    const parentItem = flattenedItems.find(({ id }) => id === item.parentId);

    if (!isDefined(parentItem)) {
      return;
    }

    if (isDefined(parentItem.parentId)) {
      handleNest(
        item,
        flattenedItems.find(({ id }) => id === parentItem.parentId),
      );

      return;
    }

    const itemIndex = flattenedItems.indexOf(item);
    const parentIndex = flattenedItems.indexOf(parentItem);

    const updatedItems = update(itemIndex, { ...item, depth: parentItem.depth - 1, parentId: null }, flattenedItems);
    const sortedItems = arrayMove(updatedItems, itemIndex, parentIndex + 1);
    const sortedTree = buildTree(sortedItems);

    onSortEnd?.(sortedTree);
    announceNestingChange(item, flattenTree(sortedTree));
  };

  return (id: Sortable.Id) => {
    const item = flattenedItems.find(({ id: itemId }) => itemId === id);

    if (!isDefined(item)) {
      return [];
    }

    const prevSibling = getPrevSibling(item);
    const nextSibling = getNextSibling(item);

    const canNestToPrevSibling = getCanNestToPrevSibling(item, prevSibling);
    const canUnNest = item.depth > 0;

    const parentItem = flattenedItems.find(({ id: itemId }) => itemId === item.parentId);

    return [
      {
        isDisabled: item.index === 0,
        key: 'move-up',
        label: t('accessibilityDescriptions:move_up'),
        onClick: () => {
          handleMoveUp(item);
        },
      },
      {
        isDisabled: !isDefined(nextSibling),
        key: 'move-down',
        label: t('accessibilityDescriptions:move_down'),
        onClick: () => {
          handleMoveDown(item);
        },
      },
      ...(canNestToPrevSibling ?
        [
          {
            key: 'nest',
            label: t('accessibilityDescriptions:nest_to_item', { item: prevSibling?.label ?? prevSibling?.id }),
            onClick: () => {
              handleNest(item, prevSibling);
            },
          },
        ]
      : []),
      ...(canUnNest ?
        [
          {
            key: 'un-nest',
            label: t('accessibilityDescriptions:un_nest_from_item', { item: parentItem?.label ?? parentItem?.id }),
            onClick: () => {
              handleUnNest(item);
            },
          },
        ]
      : []),
    ];
  };
};
